import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import {
  buildKycInfoRows,
  isKycVerified,
  normalizeRealNameStatus,
} from '../../constants/kycConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function KycInfoPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [usedList, setUsedList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      memberApi.getKycConfig(),
      memberApi.getInfo(),
      memberApi.getKycDetail(),
    ])
      .then(([config, info, kycDetail]) => {
        if (cancelled) {
          return;
        }
        const status = normalizeRealNameStatus(info?.realNameStatus || kycDetail?.realNameStatus);
        if (!isKycVerified(status)) {
          navigate('/kyc', { replace: true });
          return;
        }
        setUsedList(config?.usedList || []);
        setDetail(kycDetail || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('kyc.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, t]);

  const rows = useMemo(
    () => buildKycInfoRows(detail, usedList, t),
    [detail, usedList, t]
  );

  return (
    <div className="deposit-page kyc-page">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('kyc.infoTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      {!loading && !error && (
        <div className="kyc-info-list">
          {rows.length === 0 ? (
            <p className="deposit-page__status">{t('kyc.infoEmpty')}</p>
          ) : (
            rows.map((row) => (
              <div key={row.key} className="kyc-info-row">
                <span className="kyc-info-row__label">{row.label}</span>
                <span className="kyc-info-row__value">{row.value}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
